import { useEffect, useState } from 'react';

// kelly-notif-stale-time-ago-0915: the notification feed used to render the
// server's `time_ago` string verbatim and never touch it again, so a tab left
// open on the notification bell/page kept a minutes-old age on screen until
// the component happened to remount and refetch, at which point every age
// jumped at once (MEASURED live on web-dev.iyuga.net: a fresh "just now" row
// stayed "just now" for 100+ seconds with the page mounted, only advancing to
// "1 minute ago" after a forced reload). `created_at` is already present on
// every notification payload, so compute the label client-side from it and
// keep recomputing on a timer instead of depending on a one-shot server string.
const UNITS = [
  { unit: 'year', seconds: 31536000 },
  { unit: 'month', seconds: 2592000 },
  { unit: 'week', seconds: 604800 },
  { unit: 'day', seconds: 86400 },
  { unit: 'hour', seconds: 3600 },
  { unit: 'minute', seconds: 60 },
];

export function formatRelativeTime(dateInput, now = Date.now()) {
  if (!dateInput) return '';
  const then = new Date(dateInput).getTime();
  if (Number.isNaN(then)) return '';
  const diffSeconds = Math.floor((now - then) / 1000);
  if (diffSeconds < 45) return 'just now';
  for (const { unit, seconds } of UNITS) {
    const value = Math.floor(diffSeconds / seconds);
    if (value >= 1) {
      return `${value} ${unit}${value === 1 ? '' : 's'} ago`;
    }
  }
  const value = Math.max(1, diffSeconds);
  return `${value} second${value === 1 ? '' : 's'} ago`;
}

// Recomputes the label on an interval so it keeps ticking forward for as long
// as the component stays mounted, rather than freezing at whatever value was
// true at mount time.
export default function useRelativeTime(dateInput, { intervalMs = 15000 } = {}) {
  const [label, setLabel] = useState(() => formatRelativeTime(dateInput));

  useEffect(() => {
    setLabel(formatRelativeTime(dateInput));
    if (!dateInput) return undefined;
    const id = setInterval(() => {
      setLabel(formatRelativeTime(dateInput));
    }, intervalMs);
    return () => clearInterval(id);
  }, [dateInput, intervalMs]);

  return label;
}
